import React, {
  type FC,
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
  type ReactElement,
  type MutableRefObject,
} from "react";

import { useVirtualizer } from "@tanstack/react-virtual";

import { handleKeyboardMovement } from "./HandleKeyboard";
import {
  StyledAutocompleteBody,
  StyledAutocompleteNewItem,
  StyledAutocompleteScrollShadow,
} from "./Styles";
import highlightText from "../../hooks/useHighlightText";
import { type IOptionItemProps, Option } from "../Select/Option";
import { StyledOption } from "../Select/Option/Styles";
import { type IOptionGroupProps, OptionGroup } from "../Select/OptionGroup";

export function isOptionGroup(
  item: IOptionGroupProps | IOptionItemProps
): item is IOptionGroupProps {
  return (item as IOptionGroupProps).options !== undefined;
}

const collectLeafIds = (
  opts: (IOptionGroupProps | IOptionItemProps)[]
): (string | number)[] =>
  opts.reduce<(string | number)[]>(
    (acc, opt) =>
      isOptionGroup(opt)
        ? [...acc, ...opt.options.map((o) => o.id)]
        : [...acc, opt.id],
    []
  );

// Inserts an option at its alphabetical position among the options that share
// its checked state (checked items live at the top, so a checked option lands
// among the checked ones, sorted).
const insertAmongSameState = (
  list: IOptionItemProps[],
  item: IOptionItemProps,
  isChecked: (id: string | number) => boolean
): IOptionItemProps[] => {
  const checked = isChecked(item.id);
  let index = list.findIndex(
    (o) => isChecked(o.id) === checked && o.name.localeCompare(item.name) > 0
  );
  if (index === -1) {
    index = checked
      ? list.reduce((acc, o, i) => (isChecked(o.id) ? i + 1 : acc), 0)
      : list.length;
  }
  return [...list.slice(0, index), item, ...list.slice(index)];
};

// Frozen options that are still present keep their order (checking/unchecking
// never moves them). Everything else — newly added options, or frozen options
// that moved here (e.g. when the list is regrouped) — is slotted in among the
// checked items, alphabetically.
const reconcileFlat = (
  prev: IOptionItemProps[],
  next: IOptionItemProps[],
  frozen: Set<string | number>,
  isChecked: (id: string | number) => boolean
): IOptionItemProps[] => {
  const nextById = new Map(next.map((o) => [o.id, o]));
  const base = prev
    .filter((o) => frozen.has(o.id) && nextById.has(o.id))
    .map((o) => nextById.get(o.id) as IOptionItemProps);
  const baseIds = new Set(base.map((o) => o.id));
  const toPlace = next.filter((o) => !baseIds.has(o.id));

  return toPlace.reduce(
    (acc, item) => insertAmongSameState(acc, item, isChecked),
    base
  );
};

const reconcileOrder = (
  prev: (IOptionGroupProps | IOptionItemProps)[],
  next: (IOptionGroupProps | IOptionItemProps)[],
  frozen: Set<string | number>,
  isChecked: (id: string | number) => boolean
): (IOptionGroupProps | IOptionItemProps)[] => {
  if (!next.every(isOptionGroup) || !prev.every(isOptionGroup)) {
    return reconcileFlat(
      prev as IOptionItemProps[],
      next as IOptionItemProps[],
      frozen,
      isChecked
    );
  }

  const prevGroupById = new Map(prev.map((group) => [group.id, group]));

  return next.map((group) => ({
    ...group,
    options: reconcileFlat(
      prevGroupById.get(group.id)?.options ?? [],
      group.options,
      frozen,
      isChecked
    ),
  }));
};

export interface IAutocompleteProps {
  /** Select type: single or multiple */
  type?: "single" | "multiple";
  /** List of options or group options */
  options?: (IOptionGroupProps | IOptionItemProps)[];
  /** Selected values */
  selected?: (string | number)[] | string | number;
  /** Mixed options values */
  mixedOptions?: (string | number)[];
  /** Input Element. */
  inputEl?: MutableRefObject<HTMLInputElement | null | undefined>;
  /** Text for empty value */
  emptyValue?: string;
  /** Text for No result */
  noResultText?: string;
  /** Render option */
  renderOption?: (
    option: Record<string, ReactNode>,
    data: Record<string, unknown>
  ) => string | ReactElement;
  /** Default value that renders on top of list */
  defaultValue?: string;
  /** Sort direction for labels */
  sortDirection?: "asc" | "desc";
  /** handleChange callback */
  handleChange?: (
    e: (string | number)[] | string | number | null | undefined
  ) => void;
  /** ClassName for single option */
  optionClassName?: string;
  /** handleEmptyAction */
  handleEmptyAction?: (e: string | number) => void;
  /** Disable sorting options */
  disabledInternalSort?: boolean;
  /** ClassName for Autocomplete component */
  AutocompleteClassName?: string;
  /** handleDefaultOptionChange callback */
  handleDefaultOptionChange?: () => void;
  /** Should default value be selected */
  preselectDefaultValue?: boolean;
  /** Should order stay the same after choosing an option */
  keepSameOptionsOrder?: boolean;
  /** Set max height property for scrollbars */
  autoHeightMax?: number;
  /** Clear input on select - works in single mode */
  clearInputOnSelect?: boolean;
  /** Custom filter function for additional filtering criteria */
  filterCriteria?: (option: IOptionItemProps, filter: string) => boolean;
  disableVirtualization?: boolean;
}

interface IHover {
  item: number | string | undefined | null;
  by: "mouse" | "keyboard" | undefined;
}

interface VirtualItem {
  type: "default" | "option" | "group" | "addNew" | "noResult";
  id: string | number;
  item?: IOptionItemProps | IOptionGroupProps;
  index?: number;
}

export const Autocomplete: FC<IAutocompleteProps> = ({
  type,
  options: optionsProp = [],
  inputEl,
  selected = [],
  emptyValue,
  noResultText,
  renderOption = (option) => option?.name,
  defaultValue,
  sortDirection = "asc",
  handleChange,
  optionClassName,
  handleEmptyAction,
  disabledInternalSort,
  AutocompleteClassName,
  handleDefaultOptionChange,
  preselectDefaultValue,
  keepSameOptionsOrder = false,
  autoHeightMax = 340,
  clearInputOnSelect,
  mixedOptions = [],
  filterCriteria,
  disableVirtualization,
}) => {
  const listContainerRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLLIElement>(null);

  // Tolerate sparse/holey option arrays (some callers build them via index
  // assignment): drop holes and nullish entries so sorting never hits undefined.
  const options = useMemo(() => optionsProp.filter(Boolean), [optionsProp]);

  const selectedOptions = useMemo(() => {
    if (Array.isArray(selected)) {
      return selected;
    }
    return [selected];
  }, [selected]);

  // Ids whose position is fixed. Starts with everything present on open; a
  // freshly added option joins this set once it becomes checked, so unchecking
  // it later won't move it. Everything frozen keeps its order on every re-sort.
  const frozenRef = useRef(new Set(collectLeafIds(options)));

  const handleSort = useCallback(
    (opts) => {
      if (keepSameOptionsOrder) {
        return opts;
      }

      const isGrouped = Array.isArray(opts[0]?.options);

      const sortOptions = (a, b) => {
        const aSelected = selectedOptions.includes(a.id);
        const bSelected = selectedOptions.includes(b.id);
        const aMixed = mixedOptions.includes(a.id);
        const bMixed = mixedOptions.includes(b.id);

        if (aSelected && !bSelected) {
          return -1;
        }
        if (!aSelected && bSelected) {
          return 1;
        }
        if (aMixed && !bMixed) {
          return -1;
        }
        if (!aMixed && bMixed) {
          return 1;
        }
        return 0;
      };

      if (isGrouped) {
        return opts.map((group) => ({
          ...group,
          options: [...group.options].sort(sortOptions),
        }));
      } else {
        return [...opts].sort(sortOptions);
      }
    },
    [keepSameOptionsOrder, selectedOptions, mixedOptions]
  );

  const sortList = useCallback(
    (options) => {
      if (disabledInternalSort) {
        return handleSort(options);
      }

      const alphaSort = (a, b) =>
        sortDirection === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);

      const isGrouped = Array.isArray(options[0]?.options);

      if (isGrouped) {
        const sortedOptions = options.map((group) => ({
          ...group,
          options: handleSort([...group.options].sort(alphaSort)),
        }));

        return handleSort(sortedOptions);
      } else {
        return handleSort([...options].sort(alphaSort));
      }
    },
    [sortDirection, disabledInternalSort, handleSort]
  );

  const [sortedList, setSortedList] = useState(() => sortList(options));

  // The order is established on open. Options present then keep their place for
  // the whole session — checking/unchecking never reorders them. Options added
  // afterwards are slotted in among the checked items, alphabetically. The
  // selection key is a primitive so a fresh `selected = []` default can't loop.
  const isInitialSortRef = useRef(true);
  const selectedKey = selectedOptions.join(",");

  useEffect(() => {
    if (isInitialSortRef.current) {
      isInitialSortRef.current = false;
      return;
    }

    if (keepSameOptionsOrder) {
      setSortedList(sortList(options));
      return;
    }

    if (sortedList.length === 0) {
      frozenRef.current = new Set(collectLeafIds(options));
      setSortedList(sortList(options));
      return;
    }

    const isChecked = (id: string | number) => selectedOptions.includes(id);
    const frozen = frozenRef.current;

    const nextFrozen = new Set(frozen);
    collectLeafIds(options).forEach((id) => {
      if (!frozen.has(id) && isChecked(id)) {
        nextFrozen.add(id);
      }
    });
    frozenRef.current = nextFrozen;

    setSortedList(reconcileOrder(sortedList, options, frozen, isChecked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, selectedKey]);

  const [hover, setHover] = useState<IHover>({
    item: undefined,
    by: undefined,
  });
  const [filter, setFilter] = useState("");

  const handleEmpty = useCallback(
    (e) => {
      if (e && e.button !== 0) {
        return;
      }
      if (handleEmptyAction) {
        inputEl?.current?.focus();
        setFilter("");
        handleEmptyAction(filter);
      }
    },
    [filter, handleEmptyAction, inputEl]
  );

  const onAddNewMouseEnter = useCallback(
    () => setHover({ item: "addNew", by: "mouse" }),
    []
  );

  const showAddNew = useMemo(() => {
    return !!(
      emptyValue &&
      filter.trim() &&
      options.every((option) => {
        if (isOptionGroup(option)) {
          return option.options.every(
            (v) => v.name.toLowerCase() !== filter.trim().toLowerCase()
          );
        }
        return option.name.toLowerCase() !== filter.trim().toLowerCase();
      })
    );
  }, [emptyValue, filter, options]);

  const filterOptions = useCallback(
    (options, filter) => {
      const trimmedFilter = filter.trim();
      const isGrouped = options[0] && isOptionGroup(options[0]);

      const matchesFilter = (option: IOptionItemProps) => {
        if (option.hidden) {
          return false;
        }

        const nameMatch = option.name
          .toLowerCase()
          .includes(trimmedFilter.toLowerCase());

        if (filterCriteria && trimmedFilter) {
          return nameMatch || filterCriteria(option, trimmedFilter);
        }

        return nameMatch;
      };

      if (isGrouped) {
        let hovered = false;
        return options.reduce((acc, groupedOption) => {
          const filteredOptions = groupedOption.options.filter(matchesFilter);

          if (filteredOptions.length > 0) {
            if (!hovered && filter) {
              setHover({ item: filteredOptions[0].id, by: "keyboard" });
            }
            hovered = true;
            return [...acc, { ...groupedOption, options: filteredOptions }];
          }
          return [...acc];
        }, []);
      } else {
        const filteredOptions = options.filter(matchesFilter);

        if (filter && filteredOptions.length > 0) {
          setHover({ item: filteredOptions[0].id, by: "keyboard" });
        }
        if (filteredOptions.length === 0 && emptyValue) {
          setHover({ item: "addNew", by: "keyboard" });
        }
        return filteredOptions;
      }
    },
    [emptyValue, filterCriteria]
  );

  const list = useMemo(
    () => filterOptions(sortedList, filter),
    [filter, filterOptions, sortedList]
  );

  const showDefaultOption = useMemo(
    () => !!defaultValue && !filter,
    [defaultValue, filter]
  );

  const flatOptions = useMemo(() => {
    const options = list.reduce(
      (
        acc: (IOptionItemProps | IOptionGroupProps | [])[],
        option: IOptionItemProps | IOptionGroupProps
      ) => {
        if (!isOptionGroup(option)) {
          return [...acc, option];
        }
        return [...acc, ...option.options];
      },
      []
    );
    return filterOptions(options, filter);
  }, [filter, filterOptions, list]);

  const showNoResultCondition = useMemo(
    () =>
      noResultText &&
      !showAddNew &&
      list.length < 1 &&
      ((defaultValue && filter) || !defaultValue),
    [noResultText, showAddNew, list.length, defaultValue, filter]
  );

  const virtualItems = useMemo(() => {
    const items: VirtualItem[] = [];

    if (showDefaultOption) {
      items.push({
        type: "default",
        id: "default",
      });
    }

    list.forEach((item, index) => {
      if (isOptionGroup(item)) {
        items.push({
          type: "group",
          id: item.id,
          item,
        });

        item.options.forEach((option) => {
          items.push({
            type: "option",
            id: option.id,
            item: option,
            index,
          });
        });
      } else {
        items.push({
          type: "option",
          id: item.id,
          item,
          index,
        });
      }
    });

    if (showNoResultCondition) {
      items.push({
        type: "noResult",
        id: "noResult",
      });
    }

    if (showAddNew) {
      items.push({
        type: "addNew",
        id: "addNew",
      });
    }

    return items;
  }, [list, showDefaultOption, showNoResultCondition, showAddNew]);

  const shouldUseVirtualization =
    !disableVirtualization && virtualItems.length > 40;

  const rowVirtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => listContainerRef.current,
    estimateSize: () => 28,
    overscan: 5,
    gap: 4,
  });

  const handleInputChange = useCallback((e) => {
    if (
      e.target &&
      !(e.key === "ArrowDown") &&
      !(e.key === "ArrowUp") &&
      !(e.key === "Enter")
    ) {
      setFilter(e.target.value);
    }
  }, []);

  const handleHoverCallback = useCallback(
    (e: number | string | undefined): void => {
      setHover({ item: e, by: "mouse" });
    },
    []
  );

  const toggleSelected = useCallback(
    (id) => {
      let result;

      if (id !== null) {
        if (type === "multiple") {
          if (selectedOptions.includes(id)) {
            result = selectedOptions.filter((_id) => _id !== id);
          } else {
            result = [...selectedOptions, id];
          }
        } else {
          result = id;
        }
        if (clearInputOnSelect && inputEl?.current) {
          inputEl.current.value = "";
          inputEl.current.dispatchEvent(new Event("change", { bubbles: true }));
        }
        setFilter("");
      } else {
        if (typeof handleDefaultOptionChange === "function") {
          handleDefaultOptionChange();
          return;
        }
      }

      if (typeof handleChange === "function") {
        handleChange(result);
      }
    },
    [
      clearInputOnSelect,
      handleChange,
      handleDefaultOptionChange,
      inputEl,
      selectedOptions,
      type,
    ]
  );

  const handleMouseEnter = useCallback((e) => {
    if (e === undefined || e === null) {
      return setHover({ item: null, by: "mouse" });
    }
    setHover({ item: e, by: "mouse" });
  }, []);

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      toggleSelected(hover.item);
    },
    [toggleSelected, hover]
  );

  const handleRenderOption = useCallback(
    (item: IOptionItemProps | IOptionGroupProps, index) => {
      if (isOptionGroup(item)) {
        return (
          <OptionGroup
            checked={selectedOptions}
            name={item.name}
            icon={item.icon}
            tooltip={item.tooltip}
            key={item.id}
            setHover={handleHoverCallback}
            id={item.id}
            hover={hover.item}
            options={item.options}
            renderOptions={handleRenderOption}
            type={type}
            onChange={handleChange}
            filter={filter}
            mixedOptions={mixedOptions}
          />
        );
      }
      return (
        <Option
          name={item.name}
          ref={hover.item === item.id ? itemRef : null}
          key={index}
          tooltip={item.tooltip}
          onMouseEnter={handleMouseEnter}
          onClick={handleClick}
          id={item.id}
          hover={item.id === hover.item}
          className={optionClassName}
          renderOption={renderOption(
            { ...item, name: highlightText(item.name, filter) },
            {
              id: `option_${item.id}`,
              checked: selectedOptions && selectedOptions.includes(item.id),
              hover: hover.item === item.id,
              onChange: () => null,
            }
          )}
        />
      );
    },
    [
      handleClick,
      handleMouseEnter,
      hover.item,
      optionClassName,
      renderOption,
      filter,
      selectedOptions,
      handleHoverCallback,
      type,
      handleChange,
      mixedOptions,
    ]
  );

  const handleOnMouseLeave = useCallback(() => {
    setHover({ item: undefined, by: "mouse" });
  }, []);

  useEffect(() => {
    if (hover.by === "keyboard" && hover.item !== undefined) {
      const index = virtualItems.findIndex((item) => item.id === hover.item);
      if (index !== -1) {
        rowVirtualizer.scrollToIndex(index, { align: "center" });
      }
    }
  }, [hover, rowVirtualizer, virtualItems]);

  const handleOnKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();

        if (hover.item === undefined && filter === "") {
          return;
        }
        if (hover.item === "addNew") {
          handleEmpty(null);
          return;
        }
        if (hover.item === null && handleDefaultOptionChange) {
          handleDefaultOptionChange();
          return;
        }

        if (typeof hover.item !== "undefined" || hover.item !== null) {
          toggleSelected(hover.item);
          setFilter("");
        }
        return;
      }
      setHover({
        item: handleKeyboardMovement(
          e,
          hover.item,
          flatOptions,
          showAddNew,
          showDefaultOption
        ),
        by: "keyboard",
      });
    },
    [
      filter,
      flatOptions,
      toggleSelected,
      handleDefaultOptionChange,
      handleEmpty,
      hover,
      showAddNew,
      showDefaultOption,
    ]
  );

  useEffect(() => {
    const listenerTarget = inputEl?.current;
    if (!listenerTarget) return;

    listenerTarget.addEventListener("input", handleInputChange);
    listenerTarget.addEventListener("keydown", handleOnKeyDown);
    return () => {
      listenerTarget.removeEventListener("input", handleInputChange);
      listenerTarget.removeEventListener("keydown", handleOnKeyDown);
    };
  }, [handleInputChange, handleOnKeyDown, inputEl]);

  const isDefaultOptionSelected = useCallback(() => {
    if (preselectDefaultValue) {
      return selectedOptions.length < 1 || selectedOptions[0] === "";
    } else {
      return (
        selectedOptions[0] === null ||
        selectedOptions.length === flatOptions.length
      );
    }
  }, [flatOptions.length, preselectDefaultValue, selectedOptions]);

  const renderVirtualItem = useCallback(
    (virtualItem: VirtualItem, index: number) => {
      switch (virtualItem.type) {
        case "default":
          return (
            <Option
              name={String(defaultValue)}
              ref={hover.item === null ? itemRef : null}
              hover={hover.item === null}
              onMouseEnter={handleMouseEnter}
              onClick={(e) => {
                e.preventDefault();
                toggleSelected(null);
              }}
              renderOption={renderOption(
                { name: defaultValue, id: null },
                {
                  checked: isDefaultOptionSelected(),
                  hover: hover.item === null,
                  onChange: () => null,
                }
              )}
            />
          );

        case "group":
          if (!virtualItem.item || !isOptionGroup(virtualItem.item))
            return null;
          return (
            <OptionGroup
              checked={selectedOptions}
              name={virtualItem.item.name}
              icon={virtualItem.item.icon}
              tooltip={virtualItem.item.tooltip}
              key={virtualItem.item.id}
              setHover={handleHoverCallback}
              id={virtualItem.item.id}
              hover={hover.item}
              options={virtualItem.item.options}
              renderOptions={handleRenderOption}
              type={type}
              onChange={handleChange}
              filter={filter}
              mixedOptions={mixedOptions}
            />
          );

        case "option":
          if (!virtualItem.item || isOptionGroup(virtualItem.item)) return null;
          return (
            <Option
              name={(virtualItem.item as IOptionItemProps).name}
              ref={hover.item === virtualItem.id ? itemRef : null}
              key={index}
              tooltip={(virtualItem.item as IOptionItemProps).tooltip}
              onMouseEnter={() => handleMouseEnter(virtualItem.id)}
              onClick={handleClick}
              id={virtualItem.id}
              hover={virtualItem.id === hover.item}
              className={optionClassName}
              renderOption={renderOption(
                {
                  ...(virtualItem.item as IOptionItemProps),
                  name: highlightText(
                    (virtualItem.item as IOptionItemProps).name,
                    filter
                  ),
                },
                {
                  id: `option_${virtualItem.id}`,
                  checked:
                    selectedOptions && selectedOptions.includes(virtualItem.id),
                  hover: hover.item === virtualItem.id,
                  onChange: () => null,
                }
              )}
            />
          );

        case "noResult":
          return <StyledOption css="cursor: auto">{noResultText}</StyledOption>;

        case "addNew":
          return (
            <StyledAutocompleteNewItem
              ref={hover.item === "addNew" ? itemRef : null}
              key="emptyValue"
              hover={hover.item === "addNew"}
              onMouseDown={handleEmpty}
              onMouseEnter={onAddNewMouseEnter}
            >
              {emptyValue}
            </StyledAutocompleteNewItem>
          );

        default:
          return null;
      }
    },
    [
      defaultValue,
      hover.item,
      handleMouseEnter,
      renderOption,
      isDefaultOptionSelected,
      selectedOptions,
      handleHoverCallback,
      handleRenderOption,
      type,
      handleChange,
      filter,
      mixedOptions,
      handleClick,
      optionClassName,
      noResultText,
      handleEmpty,
      onAddNewMouseEnter,
      emptyValue,
      toggleSelected,
    ]
  );

  return (
    <StyledAutocompleteScrollShadow
      className={AutocompleteClassName}
      $isHidden={
        !defaultValue && !emptyValue && !noResultText && list.length < 1
      }
    >
      {({ onScroll }) => (
        <StyledAutocompleteBody key="body" onMouseLeave={handleOnMouseLeave}>
          <div
            ref={listContainerRef}
            style={{
              height: `${Math.min(
                rowVirtualizer.getTotalSize() + 12,
                autoHeightMax
              )}px`,
              overflow: "auto",
            }}
            onScroll={(e) => {
              if (onScroll) onScroll(e);
            }}
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              {shouldUseVirtualization
                ? rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const item = virtualItems[virtualRow.index];
                    return (
                      <div
                        key={virtualRow.index}
                        data-index={virtualRow.index}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {renderVirtualItem(item, virtualRow.index)}
                      </div>
                    );
                  })
                : virtualItems.map((item, index) => (
                    <React.Fragment key={index}>
                      {renderVirtualItem(item, index)}
                    </React.Fragment>
                  ))}
            </div>
          </div>
        </StyledAutocompleteBody>
      )}
    </StyledAutocompleteScrollShadow>
  );
};

Autocomplete.displayName = "Autocomplete";
